/**
 * Backgrounds as structure rather than CSS. A palette is design data; CSS is
 * one rendering of it. Each platform composes its own — `toCss` for the web,
 * SVG on native — so no surface has to parse or approximate the other's.
 */

export type LengthUnit = 'px' | '%' | 'none';

/** A CSS length. `none` is a bare `0`, which carries no unit. */
export interface Length {
  value: number;
  unit: LengthUnit;
}

export interface ColorStop {
  color: string;
  /** Absent lets the gradient place it; two positions are a hard band. */
  at?: [Length] | [Length, Length];
}

export interface SolidLayer {
  type: 'solid';
  color: string;
}

export interface LinearLayer {
  type: 'linear';
  /** Degrees, in CSS's convention: 0 points up, angles run clockwise. */
  angle: number;
  /** Tiles its stop list forever instead of stretching it once. */
  repeating?: boolean;
  stops: ColorStop[];
}

export interface RadialLayer {
  type: 'radial';
  shape?: 'circle' | 'ellipse';
  /** Explicit radii, for an ellipse that is not sized by its container. */
  radii?: [Length, Length];
  center?: [Length, Length];
  stops: ColorStop[];
  /** Makes the layer a repeating tile rather than one painted gradient. */
  tile?: { position: [Length, Length]; size: [Length, Length] };
}

export type BackgroundLayer = SolidLayer | LinearLayer | RadialLayer;

/** Authoring shorthands, so an entry reads close to the CSS it replaces. */
export const px = (value: number): Length => ({ value, unit: 'px' });
export const pct = (value: number): Length => ({ value, unit: '%' });
/** A bare `0`, which CSS accepts without a unit. */
export const zero: Length = { value: 0, unit: 'none' };

const len = ({ value, unit }: Length) =>
  unit === 'none' ? `${value}` : `${value}${unit}`;

const pair = (p: [Length, Length]) => `${len(p[0])} ${len(p[1])}`;

const stop = ({ color, at }: ColorStop) =>
  at ? `${color} ${at.map(len).join(' ')}` : color;

function layerToCss(layer: BackgroundLayer): string {
  if (layer.type === 'solid') return layer.color;

  const stops = layer.stops.map(stop).join(', ');

  if (layer.type === 'linear') {
    const fn = layer.repeating
      ? 'repeating-linear-gradient'
      : 'linear-gradient';
    return `${fn}(${layer.angle}deg, ${stops})`;
  }

  const prelude = [
    layer.shape,
    layer.radii && pair(layer.radii),
    layer.center && `at ${pair(layer.center)}`,
  ]
    .filter(Boolean)
    .join(' ');

  const gradient = `radial-gradient(${prelude ? `${prelude}, ` : ''}${stops})`;

  return layer.tile
    ? `${gradient} ${pair(layer.tile.position)} / ${pair(layer.tile.size)}`
    : gradient;
}

/** As a `background` shorthand. First layer paints on top, as CSS orders it. */
export function toCss(layers: BackgroundLayer[]): string {
  return layers.map(layerToCss).join(', ');
}

const HEX6 = /^#[0-9a-fA-F]{6}$/;

/** Solids and gradient stops alike. Alpha colours only tint, so they are out. */
export function opaqueColors(layers: BackgroundLayer[]): string[] {
  return layers
    .flatMap(layer =>
      layer.type === 'solid'
        ? [layer.color]
        : layer.stops.map(({ color }) => color),
    )
    .filter(color => HEX6.test(color));
}
